// Authenticated CSV export for the OC history.
//
// RTK Query's baseQuery parses JSON and sets Content-Type: application/json,
// which doesn't fit a binary/text blob download. The backend also returns the
// filename via Content-Disposition, so we do a one-off authenticated fetch here
// (same spirit as the PDF's doc.save() living outside RTK).

import { OcOrdersQueryParams } from "../../types/oc";
import { parseCsvFilename, triggerBlobDownload } from "../csvDownload";

// Filters shared with the table listing — note: no page/pageSize, export is full.
export type OcCsvExportFilters = Pick<
  OcOrdersQueryParams,
  "clientId" | "issuerCompanyId" | "mode" | "startDate" | "endDate" | "search"
>;

const FALLBACK_FILENAME = "historico-ocs.csv";

// Mirrors the param-building in ocApi.getOcOrders (sans pagination).
const buildExportQuery = (filters: OcCsvExportFilters): string => {
  const search = new URLSearchParams();
  const { clientId, issuerCompanyId, mode, startDate, endDate, search: q } =
    filters;
  if (clientId) search.append("clientId", clientId);
  if (issuerCompanyId) search.append("issuerCompanyId", issuerCompanyId);
  if (mode) search.append("mode", mode);
  if (startDate) search.append("startDate", startDate);
  if (endDate) search.append("endDate", endDate);
  if (q) search.append("search", q);
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

/**
 * Downloads the OC history as a CSV file, applying the active table filters and
 * sending the Authorization Bearer token. Throws on a non-OK response so the
 * caller can surface a toast.
 */
export const exportOcOrdersCsv = async (
  filters: OcCsvExportFilters,
  token: string
): Promise<void> => {
  const url =
    `${process.env.REACT_APP_API_BACKEND_URL}/api/oc/orders/export/csv` +
    buildExportQuery(filters);

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Export failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const filename = parseCsvFilename(
    response.headers.get("Content-Disposition"),
    FALLBACK_FILENAME
  );
  triggerBlobDownload(blob, filename);
};
